import type { StoreScorecard as Scorecard, StoreTrend } from "@khazzin/api-client";
import type { Locale, Translate } from "@khazzin/i18n";
import type { UseQueryResult } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import type { ReactNode } from "react";
import { BarSeries, ChartCard, type ChartData, LineTrend, StackedMeter } from "../charts";
import { KpiTile } from "../patterns/page";
import { StatusChip } from "../patterns/StatusChip";
import { formatCount, formatDay, formatRatio } from "./format";
import { Loaded } from "./SalesPanels";
import {
	amountsText,
	HEALTH_TONE,
	STORE_UNIT_KINDS,
	scorecardTrendRows,
	storageRows,
} from "./stores";

/**
 * One store's Store Performance scorecard (EP13-S11, DA-13-07): eight tiles
 * (orders, revenue, fulfillment and on-SLA rates, return rate, storage
 * utilisation, fees billed and net payout), orders and the fulfillment rate
 * per day, and the storage meter by unit kind.
 *
 * Shared by the Admin Console's `/analytics/stores/:storeId` and the Store
 * Portal's Scorecard tab. `variant="store"` draws no health chip; the server
 * already sends a store no rank, no chip and no building capacity.
 */

export interface StoreScorecardProps {
	t: Translate;
	locale: Locale;
	variant: "admin" | "store";
	scorecard: UseQueryResult<Scorecard>;
	trend: UseQueryResult<StoreTrend>;
	/** The admin's link to the store's Billing tab (EP01-S12). */
	billing?: ReactNode;
}

export function StoreScorecard({
	t,
	locale,
	variant,
	scorecard,
	trend,
	billing,
}: StoreScorecardProps) {
	const card = scorecard.data?.scorecard;
	const day = (value: string) => formatDay(value, locale);
	const ratio = (bps: number | null) => formatRatio(bps, locale);
	const count = (value: number) => formatCount(value, locale);
	// A tenant's own kind or bin unit comes with its name.
	const kindLabel = (kind: string) =>
		(STORE_UNIT_KINDS as readonly string[]).includes(kind)
			? t(`analytics.stores.unitKinds.${kind}`)
			: (card?.storage?.kinds.find((k) => k.unitKind === kind)?.name ?? kind);
	const rows = scorecardTrendRows(trend.data);
	const range = scorecard.data;
	// The meter reads each kind's share out of 10,000, so a store's card (no
	// capacity) draws the same meter as an operator's.
	const storage: ChartData = {
		x: { key: "kind", label: t("analytics.stores.storage.kind"), format: kindLabel },
		series: [{ key: "used", label: t("analytics.stores.storage.used"), color: 1 }],
		rows: storageRows(card),
		format: (value) => formatRatio(value, locale),
	};

	return (
		<div className="flex flex-col gap-4">
			<Loaded query={scorecard} t={t}>
				{card !== undefined && (
					<>
						{variant === "admin" && card.health !== null && (
							<div className="flex flex-wrap items-center gap-2 text-[12.5px] text-fg-muted">
								<span>{t("analytics.stores.health.label")}</span>
								<StatusChip
									tone={HEALTH_TONE[card.health]}
									{...(card.health === "risk" ? { icon: AlertTriangle } : {})}
								>
									{t(`analytics.stores.health.${card.health}`)}
								</StatusChip>
							</div>
						)}
						<div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="scorecard-tiles">
							<KpiTile
								t={t}
								status="ready"
								label={t("analytics.stores.tiles.orders")}
								value={count(card.orders)}
							/>
							<KpiTile
								t={t}
								status="ready"
								label={t("analytics.stores.tiles.revenue")}
								value={amountsText(card.revenue, locale)}
							/>
							<Tile
								label={t("analytics.stores.tiles.fulfillment")}
								value={ratio(card.fulfillmentRateBps)}
								note={t("analytics.stores.notes.fulfillment", {
									packed: count(card.packed),
									received: count(card.received),
								})}
								t={t}
							/>
							<Tile
								label={t("analytics.stores.tiles.onSla")}
								value={ratio(card.onSlaRateBps)}
								note={t("analytics.stores.notes.onSla", {
									onSla: count(card.onSla),
									judged: count(card.slaJudged),
								})}
								t={t}
							/>
							<Tile
								label={t("analytics.stores.tiles.returns")}
								value={ratio(card.returnRateBps)}
								note={t("analytics.stores.notes.returns", {
									returns: count(card.returns),
									handedOff: count(card.handedOff),
								})}
								t={t}
							/>
							<Tile
								label={t("analytics.stores.tiles.storage")}
								value={ratio(card.storageUtilizationBps)}
								note={
									card.storage === null
										? t("analytics.stores.notes.noSnapshot")
										: t("analytics.stores.notes.storage", { date: day(card.storage.date) })
								}
								t={t}
							/>
							<Tile
								label={t("analytics.stores.tiles.fees")}
								value={amountsText(card.feesBilled, locale)}
								note={statementsNote(card.statements, t)}
								t={t}
							/>
							<Tile
								label={t("analytics.stores.tiles.payout")}
								value={amountsText(card.netPayout, locale)}
								note={statementsNote(card.statements, t)}
								t={t}
							/>
						</div>
						{billing}
					</>
				)}
			</Loaded>
			<div className="grid gap-4 lg:grid-cols-2">
				<Loaded query={trend} t={t}>
					<TrendCard
						title={t("analytics.stores.trend.orders")}
						summary={
							range === undefined
								? t("analytics.stores.trend.orders")
								: t("analytics.stores.trend.ordersSummary", {
										from: day(range.from),
										to: day(range.to),
									})
						}
						data={{
							x: { key: "date", label: t("analytics.stores.trend.day"), format: day },
							series: [{ key: "orders", label: t("analytics.stores.tiles.orders"), color: 1 }],
							rows,
							format: count,
						}}
						t={t}
						kind="bars"
					/>
				</Loaded>
				<Loaded query={trend} t={t}>
					<TrendCard
						title={t("analytics.stores.trend.fulfillment")}
						summary={t("analytics.stores.trend.fulfillment")}
						data={{
							x: { key: "date", label: t("analytics.stores.trend.day"), format: day },
							series: [{ key: "rate", label: t("analytics.stores.tiles.fulfillment"), color: 2 }],
							rows,
							format: (value) => formatRatio(value * 100, locale),
						}}
						t={t}
						kind="line"
					/>
				</Loaded>
			</div>
			<Loaded query={scorecard} t={t}>
				{card?.storage != null && (
					<ChartCard
						title={t("analytics.stores.storage.title")}
						summary={t("analytics.stores.storage.summary", { date: day(card.storage.date) })}
						data={storage}
						t={t}
					>
						<StackedMeter
							{...storage}
							totalKey="total"
							formatShare={(fraction) => formatRatio(Math.round(fraction * 10000), locale)}
						/>
						<ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-fg-muted">
							{card.storage.kinds.map((k) => (
								<li key={k.unitKind}>
									{t("analytics.stores.storage.positions", {
										kind: kindLabel(k.unitKind),
										count: count(k.occupied),
									})}
								</li>
							))}
						</ul>
					</ChartCard>
				)}
			</Loaded>
		</div>
	);
}

function statementsNote(statements: number, t: Translate): string {
	return statements === 0
		? t("analytics.stores.notes.noStatement")
		: t("analytics.stores.notes.statements", { count: statements });
}

/** A tile with its one-line note under the figure. */
function Tile({
	label,
	value,
	note,
	t,
}: {
	label: string;
	value: string;
	note: string;
	t: Translate;
}) {
	return (
		<div className="flex min-w-0 flex-col card">
			<KpiTile t={t} className="border-0 shadow-none" status="ready" label={label} value={value} />
			<p className="px-4 pb-3 text-[11.5px] text-fg-muted">{note}</p>
		</div>
	);
}

function TrendCard({
	title,
	summary,
	data,
	t,
	kind,
}: {
	title: string;
	summary: string;
	data: ChartData;
	t: Translate;
	kind: "bars" | "line";
}) {
	return (
		<ChartCard title={title} summary={summary} data={data} t={t}>
			{kind === "bars" ? <BarSeries {...data} /> : <LineTrend {...data} />}
		</ChartCard>
	);
}
